import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import EmbeddedAdminLayout from "../components/EmbeddedAdminLayout";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { 
  User, Mail, Phone, Save, Loader2, Shield, Building2, Plus, 
  ExternalLink, Crown, ChevronRight, Settings
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const AdminProfile = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedAdminLayout : AdminLayout;
  const navigate = useNavigate();
  const { user, token, setUser, switchRole } = useAuth();
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [newOrgData, setNewOrgData] = useState({
    name: "",
    email: "",
    phone: "",
    subdomain: "",
    description: ""
  });

  useEffect(() => {
    if (user && token) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || ""
      });
      fetchOrganizations();
    }
  }, [user, token]);

  const fetchOrganizations = async () => {
    if (!token) return;
    setLoadingOrgs(true);
    try {
      const response = await axios.get(`${API}/organizations/my-organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      // Fallback to single org if the new endpoint doesn't exist
      if (user?.org_id) {
        try {
          const orgResponse = await axios.get(`${API}/organizations/${user.org_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOrganizations([{ ...orgResponse.data, user_role: "admin" }]);
        } catch (e) {
          console.error("Error fetching single org:", e);
        }
      }
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        name: formData.name,
        phone: formData.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    
    if (!newOrgData.name.trim()) {
      toast.error("Organization name is required");
      return;
    }
    if (!newOrgData.email.trim()) {
      toast.error("Organization email is required");
      return;
    }

    setCreatingOrg(true);
    try {
      const response = await axios.post(`${API}/organizations/register`, {
        name: newOrgData.name,
        email: newOrgData.email,
        phone: newOrgData.phone || null,
        subdomain: newOrgData.subdomain || null,
        description: newOrgData.description || null,
        subscription_tier: "free",
        billing_cycle: "monthly"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message || "Organization created successfully!");
      setShowCreateOrg(false);
      setNewOrgData({ name: "", email: "", phone: "", subdomain: "", description: "" });
      fetchOrganizations();
      
      // If the org was auto-approved (free tier pending), show additional info
      if (response.data.requires_approval) {
        toast.info("Your organization is pending approval by an administrator.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organization");
    } finally {
      setCreatingOrg(false);
    }
  };

  const switchToOrganization = async (org) => {
    try {
      if (org.org_id !== user?.org_id) {
        // Call switchRole to get a new JWT with the correct org_id
        await switchRole('org_admin', org.org_id);
        toast.success(`Switched to ${org.name}`);
      }
      navigate("/admin");
    } catch (error) {
      toast.error("Failed to switch organization");
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      owner: { label: "Owner", className: "bg-amber-100 text-amber-700" },
      admin: { label: "Admin", className: "bg-blue-100 text-blue-700" },
      manager: { label: "Manager", className: "bg-green-100 text-green-700" }
    };
    const badge = badges[role] || badges.admin;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: "Active", className: "bg-green-100 text-green-700" },
      pending: { label: "Pending", className: "bg-amber-100 text-amber-700" },
      suspended: { label: "Suspended", className: "bg-red-100 text-red-700" }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <PageLayout title="My Profile">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Profile Settings Card */}
        <Card className="border-slate-200/60 rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Settings
            </CardTitle>
            <CardDescription>
              Manage your account information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Avatar */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">{user?.name?.charAt(0) || 'A'}</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Shield className="w-3 h-3 text-slate-400" />
                    <span className="text-xs text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</span>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 rounded-xl"
                    placeholder="Your name"
                  />
                </div>
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    value={formData.email}
                    disabled
                    className="pl-10 rounded-xl bg-slate-50"
                    placeholder="Email"
                  />
                </div>
                <p className="text-xs text-slate-500">Email cannot be changed</p>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10 rounded-xl"
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                className="w-full rounded-xl"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* My Organizations Card */}
        <Card className="border-slate-200/60 rounded-2xl shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  My Organizations
                </CardTitle>
                <CardDescription>
                  Organizations you manage
                </CardDescription>
              </div>
              <Button 
                onClick={() => setShowCreateOrg(true)}
                className="rounded-xl"
                data-testid="create-org-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Organization
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingOrgs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : organizations.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 mb-4">You don't manage any organizations yet</p>
                <Button 
                  onClick={() => setShowCreateOrg(true)}
                  variant="outline"
                  className="rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Organization
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {organizations.map((org) => (
                  <div 
                    key={org.org_id}
                    className={`flex items-center justify-between p-4 rounded-2xl border transition-colors ${
                      org.org_id === user?.org_id 
                        ? 'bg-slate-900 text-white border-slate-900' 
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {org.logo_url ? (
                        <img 
                          src={org.logo_url} 
                          alt={org.name}
                          className="w-12 h-12 rounded-xl object-contain bg-white"
                        />
                      ) : (
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          org.org_id === user?.org_id ? 'bg-slate-800' : 'bg-slate-200'
                        }`}>
                          <Building2 className={`w-6 h-6 ${
                            org.org_id === user?.org_id ? 'text-slate-400' : 'text-slate-400'
                          }`} />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold ${
                            org.org_id === user?.org_id ? 'text-white' : 'text-slate-900'
                          }`}>
                            {org.name}
                          </p>
                          {org.org_id === user?.org_id && (
                            <span className="px-2 py-0.5 bg-white/20 text-white text-xs rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {getRoleBadge(org.user_role)}
                          {getStatusBadge(org.status)}
                          {org.subscription_tier && (
                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              org.subscription_tier === 'premium' 
                                ? 'bg-purple-100 text-purple-700' 
                                : org.subscription_tier === 'plus'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {org.subscription_tier === 'premium' && <Crown className="w-3 h-3" />}
                              {org.subscription_tier.charAt(0).toUpperCase() + org.subscription_tier.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {org.org_id !== user?.org_id && org.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => switchToOrganization(org)}
                          className={`rounded-xl ${
                            org.org_id === user?.org_id ? 'text-white hover:bg-slate-800' : ''
                          }`}
                        >
                          Switch
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                      {org.org_id === user?.org_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate('/admin/organization')}
                          className="text-white hover:bg-slate-800 rounded-xl"
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Manage
                        </Button>
                      )}
                      {org.subdomain && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/org/${org.subdomain}`, '_blank')}
                          className={`rounded-xl ${
                            org.org_id === user?.org_id ? 'text-white hover:bg-slate-800' : ''
                          }`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Create New Organization
            </DialogTitle>
            <DialogDescription>
              Set up a new sports organization. You'll start with a free plan.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateOrg} className="space-y-4 mt-4">
            {/* Organization Name */}
            <div className="space-y-2">
              <Label htmlFor="orgName">Organization Name *</Label>
              <Input
                id="orgName"
                value={newOrgData.name}
                onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value })}
                placeholder="e.g., Springfield Youth Sports"
                className="rounded-xl"
                required
              />
            </div>

            {/* Organization Email */}
            <div className="space-y-2">
              <Label htmlFor="orgEmail">Contact Email *</Label>
              <Input
                id="orgEmail"
                type="email"
                value={newOrgData.email}
                onChange={(e) => setNewOrgData({ ...newOrgData, email: e.target.value })}
                placeholder="contact@organization.com"
                className="rounded-xl"
                required
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="orgPhone">Phone Number</Label>
              <Input
                id="orgPhone"
                value={newOrgData.phone}
                onChange={(e) => setNewOrgData({ ...newOrgData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="rounded-xl"
              />
            </div>

            {/* Subdomain */}
            <div className="space-y-2">
              <Label htmlFor="subdomain">Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="subdomain"
                  value={newOrgData.subdomain}
                  onChange={(e) => setNewOrgData({ 
                    ...newOrgData, 
                    subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                  })}
                  placeholder="springfield"
                  className="rounded-xl"
                />
                <span className="text-sm text-slate-500 whitespace-nowrap">.sporthouse.ai</span>
              </div>
              <p className="text-xs text-slate-500">
                This will be your organization's public URL
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="orgDesc">Description</Label>
              <Input
                id="orgDesc"
                value={newOrgData.description}
                onChange={(e) => setNewOrgData({ ...newOrgData, description: e.target.value })}
                placeholder="Brief description of your organization"
                className="rounded-xl"
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm">
              <p className="text-blue-700">
                <strong>Free Plan:</strong> Your organization will start with our free plan. 
                A Sporthouse administrator will review and approve your organization within 24-48 hours.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateOrg(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creatingOrg}
                className="rounded-xl"
              >
                {creatingOrg ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Organization
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminProfile;
